import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

/**
 * Shared hook to fetch available tenants for the current user
 * Used by both DashboardHeader and TenantSwitcher to ensure consistent data
 */
export function useAvailableTenants() {
  const { user } = useAuth();
  const { isSuperAdmin, isLoading: roleLoading } = useUserRole();

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['available-tenants', user?.id, isSuperAdmin],
    queryFn: async () => {
      if (!user) {
        console.log('🏢 useAvailableTenants: No user found');
        return [];
      }

      console.log('🏢 useAvailableTenants: Fetching tenants for user:', user.id, 'isSuperAdmin:', isSuperAdmin);

      if (isSuperAdmin) {
        // Super admins can see ALL active tenants
        const { data: tenantsData, error: tenantsError } = await supabase
          .from('tenants')
          .select('id, name, slug')
          .eq('is_active', true)
          .order('name');

        if (tenantsError) {
          console.error('🏢 useAvailableTenants: Error fetching tenants for super admin:', tenantsError);
          throw tenantsError;
        }
        
        console.log('🏢 useAvailableTenants: Super admin tenants:', tenantsData?.length || 0);
        return tenantsData as Tenant[];
      } else {
        // Regular users only see tenants where they have roles
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('tenant_id')
          .eq('user_id', user.id);

        if (rolesError) {
          console.error('🏢 useAvailableTenants: Error fetching user roles:', rolesError);
          throw rolesError;
        }

        const tenantIds = [...new Set(userRoles.map(r => r.tenant_id).filter(Boolean))];
        console.log('🏢 useAvailableTenants: User tenant IDs:', tenantIds);

        if (tenantIds.length === 0) {
          console.log('🏢 useAvailableTenants: No tenant IDs found for user');
          return [];
        }

        // Fetch tenant details
        const { data: tenantsData, error: tenantsError } = await supabase
          .from('tenants')
          .select('id, name, slug')
          .in('id', tenantIds)
          .order('name');

        if (tenantsError) {
          console.error('🏢 useAvailableTenants: Error fetching tenant details:', tenantsError);
          throw tenantsError;
        }
        
        console.log('🏢 useAvailableTenants: User tenants:', tenantsData?.length || 0);
        return tenantsData as Tenant[];
      }
    },
    enabled: !!user && !roleLoading,
  });

  return {
    tenants,
    isLoading: roleLoading || tenantsLoading,
    hasMultipleTenants: tenants.length > 1,
  };
}
